import { Divider, IconButton } from '@mui/material';
import {
  Drawer,
  ListItem,
  ListItemIcon,
  styled,
  Typography,
  List as ListComponent,
  Theme,
  Radio,
  Box,
  List
} from '@mui/material';
import { fontSizes } from '../../../../layouts/fontSizes';
import pallet from '../../../../layouts/pallet';

interface NavigationDrawerProps {
  open: boolean;
  drawerWidth?: number;
}

interface ListItemProps {
  selected?: boolean;
}

const drawerWidth = 290;

const ListItemContainer = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'isOpened'
})<ListItemProps & { isOpened?: boolean }>(({ selected, isOpened }) => ({
  color: selected ? '#2B2A29' : pallet.navigationItems,
  cursor: 'pointer',
  marginBottom: isOpened ? 0 : '8px',
  '&:hover': {
    color: '#7A5AF8'
  }
}));

const StyledIconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected'
})<{ isSelected: boolean }>(({ isSelected }) => ({
  color: isSelected ? '#2B2A29' : '#AFAFAF',
  cursor: 'pointer',
  '&:hover': {
    color: '#7A5AF8'
  }
}));

const ListIconContainer = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'selected'
})<ListItemProps>(
  ({ theme, selected }: { theme: Theme; selected?: boolean }) => ({
    color: selected ? '#7A5AF8' : pallet.navigationItems,
    minWidth: '40px'
  })
);

const NestedListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'selected'
})<ListItemProps>(
  ({ theme, selected }: { theme: Theme; selected?: boolean }) => ({
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    color: selected ? '#2B2A29' : pallet.navigationItems,
    cursor: 'pointer',
    '&:hover': {
      color: '#7A5AF8'
    }
  })
);
const NestedListItemContainer = styled(List)({
  marginBottom: '20px'
});

const SidebarFooter = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'open'
})<NavigationDrawerProps>(({ theme, open }) => ({
  marginBlockStart: 'auto',
  position: 'unset',
  bottom: 0,
  width: '100%',
  textAlign: 'center',
  padding: theme.spacing(1),
  fontSize: open ? fontSizes.smallText : fontSizes.smallerText,
  color: pallet.navigationItems,
  whiteSpace: 'pre-line'
}));

const NavigationDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'drawerWidth'
})<NavigationDrawerProps>(({ theme, open, drawerWidth }) => ({
  width: open ? drawerWidth : 60,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: open ? drawerWidth : 60,
    boxShadow: 'none',
    overflow: 'visible',
    transition: theme.transitions.create(['width', 'margin'], {
      duration: theme.transitions.duration.standard,
      easing: theme.transitions.easing.easeInOut
    }),
    borderRight: `1px solid ${pallet.borderBackground}`,
    ...(open && {
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '65%',
        right: '-12px',
        width: '28px',
        height: '32px',
        transform: 'translateY(-50%)',
        background: pallet.borderBackground,
        borderRadius: '50%',
        zIndex: 1299
      }
    })
  }
}));

const NavigationItemList = styled(ListComponent)({
  paddingTop: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden'
});

const StyledEtherLogo = styled('img')({
  height: 35,
  paddingLeft: '16px',
  marginTop: '20px'
});

const StyledRadioButton = styled(Radio)(({ theme }) => ({
  color: '#EBEDF0',
  '& .MuiSvgIcon-root': {
    fontSize: fontSizes.subtitle,
    borderRadius: '50%',
    width: 10,
    height: 10,
    backgroundColor: '#EBEDF0'
  },
  '&.Mui-checked .MuiSvgIcon-root': {
    backgroundColor: '#7A5AF8',
    borderColor: '#7A5AF8'
  },
  '&.Mui-checked::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 18,
    height: 18,
    borderRadius: '50%',
    backgroundColor: 'rgba(122, 90, 248, 0.2)',
    transform: 'translate(-50%, -50%)',
    zIndex: 0,
    transition: 'opacity 0.3s ease'
  }
}));

const StyledDivider = styled(Divider)({
  marginTop: '18px',
  backgroundColor: pallet.borderBackground
});

const NavigationHeader = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  backgroundColor: theme.palette.common.white,
  zIndex: 1
}));

const NavigationContainer = styled(Box)({
  overflowY: 'auto',
  flexGrow: 1,
  paddingTop: '16px'
});

const FloatingPinButton = styled(IconButton)<{ $isPinned: boolean }>(
  ({ theme, $isPinned }) => ({
    position: 'absolute',
    top: '65%',
    right: '-12px',
    transform: `translateY(-50%) rotateY(${$isPinned ? '180deg' : '0deg'})`,
    width: 24,
    height: 24,
    padding: 0,
    borderRadius: '50%',
    background: 'linear-gradient(90deg, #A177FF 0%, #6D35F5 100%)',
    color: theme.palette.common.white,
    zIndex: 1300,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transition: 'background 0.3s ease, transform 0.6s ease',
    transformStyle: 'preserve-3d',

    '&:hover': {
      background: 'linear-gradient(90deg, #B68EFF 0%, #7B4DFD 100%)'
    },
    '&:active': {
      transform: `translateY(-50%) rotateY(${
        $isPinned ? '180deg' : '0deg'
      }) scale(0.95)`
    }
  })
);

const IconContainer = styled(Box)<{ $isPinned: boolean }>(({ $isPinned }) => ({
  position: 'relative',
  width: 10,
  height: 10,
  transformStyle: 'preserve-3d',

  '& > svg': {
    position: 'absolute',
    top: 0,
    left: 0,
    transition: 'opacity 0.3s ease'
  },

  '& > svg:first-of-type': {
    opacity: $isPinned ? 1 : 0,
    transform: 'rotateY(180deg)'
  },
  '& > svg:last-of-type': {
    opacity: $isPinned ? 0 : 1,
    transform: 'rotateY(0deg)'
  }
}));

export {
  drawerWidth,
  ListItemContainer,
  ListIconContainer,
  NestedListItem,
  SidebarFooter,
  NavigationDrawer,
  NavigationItemList,
  StyledEtherLogo,
  StyledRadioButton,
  StyledDivider,
  NavigationHeader,
  NavigationContainer,
  StyledIconWrapper,
  NestedListItemContainer,
  FloatingPinButton,
  IconContainer
};
